
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";
import type { Database } from "@/integrations/supabase/types";

type Project = Database['public']['Tables']['projects']['Row'];

export const useProjects = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [sharedProjects, setSharedProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  // Function to fetch projects with improved error handling and better logging
  const fetchProjectsWithRetry = useCallback(async () => {
    // Don't fetch if auth is still loading or no user
    if (authLoading) {
      console.log("Auth still loading, waiting...");
      return;
    }
    
    if (!user) {
      console.log("No user found, stopping project fetch");
      setIsLoading(false);
      setError(null);
      setProjects([]);
      setSharedProjects([]);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      console.log("Fetching projects for user:", user.id);
      
      // Fetch user's own projects with explicit logging
      console.log("Querying projects table for user_id:", user.id);
      const { data: ownedProjects, error: ownedError } = await supabase
        .from("projects")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (ownedError) {
        console.error("Error fetching owned projects:", ownedError);
        throw ownedError;
      }
      
      console.log("Raw projects data from database:", ownedProjects);
      console.log("Number of projects found:", ownedProjects?.length || 0);
      
      // Log each project for debugging
      if (ownedProjects && ownedProjects.length > 0) {
        ownedProjects.forEach((project, index) => {
          console.log(`Project ${index + 1}:`, {
            id: project.id,
            name: project.name,
            description: project.description,
            user_id: project.user_id,
            created_at: project.created_at
          });
        });
      }
      
      setProjects(ownedProjects || []);
      
      // Fetch projects shared with this user (accepted only)
      if (user.email || user.id) {
        console.log("Fetching shared projects for:", { email: user.email, id: user.id });
        const { data: projectShares, error: sharesError } = await supabase
          .from("project_shares")
          .select(`
            project_id,
            projects:project_id (*)
          `)
          .or(
            `and(shared_with_email.eq.${JSON.stringify(user.email)},accepted.eq.true),` +
            `and(shared_with_id.eq.${user.id},accepted.eq.true)`
          );
        if (sharesError) {
          console.error("Error fetching project shares:", sharesError);
          setSharedProjects([]);
        } else if (projectShares && projectShares.length > 0) {
          const sharedProjectsData = projectShares
            .map((share: any) => share.projects)
            .filter((p: any) => p != null);
          // Deduplicate by id
          const unique = Array.from(new Map(sharedProjectsData.map((p: any) => [p.id, p])).values());
          console.log("Shared projects data:", unique);
          setSharedProjects(unique as Project[]);
        } else {
          console.log("No shared projects found");
          setSharedProjects([]);
        }
      }
      
      // Reset retry count on success
      setRetryCount(0);
      
      // Success toast only if we actually found projects
      if (ownedProjects && ownedProjects.length > 0) {
        console.log("Successfully loaded projects:", ownedProjects.map(p => p.name));
      }
      
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      
      // Get more specific error message
      let errorMessage = "Ett fel uppstod när projekten skulle hämtas.";
      
      if (error.message?.includes("Failed to fetch") || error.code === "NETWORK_ERROR") {
        errorMessage = "Nätverksfel: Kontrollera din internetanslutning och försök igen.";
      } else if (error.code === "PGRST301" || error.message?.includes("JWT")) {
        errorMessage = "Din inloggning har gått ut. Försök logga in igen.";
      } else if (error.code === "PGRST401") {
        errorMessage = "Behörighetsproblem: Du har inte tillgång till dessa projekt.";
      } else if (error.message) {
        errorMessage = `Kunde inte hämta dina projekt: ${error.message}`;
      }
      
      setError(errorMessage);
      
      // Only retry for network errors and if we haven't exceeded max retries
      if ((error.message?.includes("Failed to fetch") || error.code === "NETWORK_ERROR") && retryCount < maxRetries) {
        console.log(`Retrying fetch (${retryCount + 1}/${maxRetries})...`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchProjectsWithRetry(), 2000);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, authLoading, retryCount]);

  // Fetch projects when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchProjectsWithRetry();
    }
  }, [fetchProjectsWithRetry, authLoading]);

  // Add project optimistically (before database confirmation)
  const addProjectOptimistically = useCallback((project: Project) => {
    setProjects(prev => [project, ...prev]);
  }, []);

  // Create new project
  const handleCreateProject = async (name: string) => {
    if (!name.trim()) {
      toast.error("Vänligen ange ett namn för projektet");
      return false;
    }

    if (!user) {
      toast.error("Du måste vara inloggad för att skapa projekt");
      return false;
    }

    try {
      console.log("Creating project for user:", user.id);
      
      const { data, error } = await supabase
        .from("projects")
        .insert([
          {
            name: name.trim(),
            description: "",
            user_id: user.id,
          },
        ])
        .select();

      if (error) {
        console.error("Error creating project:", error);
        throw error;
      }

      if (data && data.length > 0) {
        console.log("Project created:", data[0]);
        setProjects(prev => [data[0], ...prev]);
        toast.success("Projektet har skapats", {
          duration: 2000 // Kortare för snabbare UX
        });
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error creating project:", error);
      const errorMessage = error.message || "Kunde inte skapa projektet";
      setError(errorMessage);
      toast.error("Kunde inte skapa projektet: " + errorMessage);
      return false;
    }
  };

  // Edit project
  const handleEditProject = async (projectId: string, name: string) => {
    if (!name.trim()) {
      toast.error("Vänligen ange ett namn för projektet");
      return false;
    }

    try {
      console.log("Updating project:", projectId);
      
      const { data, error } = await supabase
        .from("projects")
        .update({
          name: name.trim(),
          description: "",
        })
        .eq("id", projectId)
        .select();

      if (error) {
        console.error("Error updating project:", error);
        throw error;
      }

      if (data && data.length > 0) {
        console.log("Project updated:", data[0]);
        setProjects(prev => prev.map(p => p.id === projectId ? data[0] : p));
        toast.success("Projektet har uppdaterats");
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error updating project:", error);
      const errorMessage = error.message || "Kunde inte uppdatera projektet";
      setError(errorMessage);
      toast.error("Kunde inte uppdatera projektet: " + errorMessage);
      return false;
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId: string) => {
    if (!confirm("Är du säker på att du vill ta bort detta projekt? Alla mappar och riskbedömningar kommer också att tas bort.")) {
      return false;
    }
    
    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", projectId);

      if (error) {
        console.error("Error deleting project:", error);
        throw error;
      }
      
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success("Projektet har tagits bort");
      return true;
    } catch (error: any) {
      console.error("Error deleting project:", error);
      const errorMessage = error.message || "Kunde inte ta bort projektet";
      setError(errorMessage);
      toast.error("Kunde inte ta bort projektet: " + errorMessage);
      return false;
    }
  };

  // Manually refresh projects
  const refreshProjects = useCallback(() => {
    if (!user) {
      console.log("No user for refresh");
      return;
    }
    
    console.log("Manual refresh triggered by user");
    setIsLoading(true);
    setError(null);
    setRetryCount(0);
    
    fetchProjectsWithRetry();
  }, [user, fetchProjectsWithRetry]);

  return {
    projects,
    sharedProjects,
    isLoading,
    error,
    handleCreateProject,
    handleEditProject,
    handleDeleteProject,
    refreshProjects,
    retryCount,
    maxRetries,
    addProjectOptimistically
  };
};
